import React, { memo, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

import { teamsList, createTeamGroup, getTeamGroups, assignTeamGroup, updateTeamGroup, deleteTeamGroup, createTeam } from '../../services/teams';
import { calculateRowPosition, calculateColumnPosition } from "../../utils/positionCalculations";

import { useLocalStorage } from "usehooks-ts";

import Table from "../../components/Table";
import GroupDialog from "../../components/GroupDialog";
import { InviteMemberButton } from '../../components/InviteMember';

const TeamsList = () => {
  const navigate = useNavigate();

  const [teams, setTeams] = useState([]);
  const [groupData, setGroupData] = useState(null);
  const [teamDialog, setTeamDialog] = useState(null);

  const { t } = useTranslation();

  const [groupBy, setGroupBy] = useLocalStorage("teamsGroupBy", "all");
  const [viewTeam, setViewTeam] = useLocalStorage("viewTeam", {});

  const addCoach = team => {
    console.log("Add Coach to", team);
  };

  const editTeam = team => {
    setViewTeam(team);
    navigate("/club_admin/teams/" + team?.id);
  };

  const getMyTeams = async () => {
    const { teams } = await teamsList();
    setTeams(teams);
  };

  useEffect(() => {
    getMyTeams();
  }, []);

  const closeTeamDialog = saved => {
    if (saved) {
      getMyTeams();
    }
    setTeamDialog(null);
  };

  return (
    <div className="teams">
      <div className="teams-header">
        <div className="teams-header-text">{t("teams.teams")}</div>
        <div className="teams-btn">
          {groupBy === "category" && (
            <button className="button button-add" onClick={() => setGroupData({})}>
              {t("teams.add_group")}
            </button>
          )}
          <button className="button button-add" onClick={() => setTeamDialog({})}>
            {t("teams.add")}
          </button>
        </div>
      </div>
      <div className="teams-groups">
        <div
          className={`teams-groups-option pointer ${groupBy === "all" ? "active" : ""}`}
          onClick={() => setGroupBy("all")}
        >
          {t("teams.all")}
        </div>
        <div
          className={`teams-groups-option pointer ${groupBy === "category" ? "active" : ""}`}
          onClick={() => setGroupBy("category")}
        >
          {t("teams.group")}
        </div>
      </div>
      <div className="teams-list">
        {groupBy === "all" ? (
          <TeamsAll {...{ teams, addCoach, editTeam }} />
        ) : (
          <TeamsByCategory
            {...{
              teams,
              addCoach,
              editTeam,
              groupData,
              setGroupData,
              fetchRows: getMyTeams
            }}
          />
        )}
      </div>
      {teamDialog && <TeamDialog team={teamDialog} close={saved => closeTeamDialog(saved)} />}
    </div>
  );
};

const TeamDialog = ({ team, close }) => {
  const { t } = useTranslation();

  const [form, setForm] = useState({ ...team, name: team?.name || "", age: team?.age || 10 });

  const save = async () => {
    try {
      await createTeam(form);
      performClose(true);
    } catch (e) {}
  };

  const performClose = (reload = false) => {
    close && close(reload);
  };

  return (
    <div className="dialog">
      <div className="dialog-content">
        <div className="close" onClick={performClose}></div>
        <div className="form-group">
          <label htmlFor="groupName">{t("common.table_name")}</label>
          <input
            id="name"
            type="text"
            value={form.name}
            required
            onChange={e => setForm({ ...form, name: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label htmlFor="groupName">{t("teams.age")}</label>
          <input
            id="age"
            type="number"
            min={5}
            max={15}
            required
            value={form.age}
            onChange={e => setForm({ ...form, age: e.target.value })}
          />
        </div>
        <div className="form-container-actions">
          <div className="flex-end">
            <button className="button" onClick={performClose}>
              {t("buttons.common.cancel")}
            </button>
            <button className="button button-primary" onClick={save}>
              {t("buttons.common.save")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(TeamsList);

const TeamsAll = ({ teams, editTeam }) => {
  const { t } = useTranslation();

  const columns = React.useMemo(
    () => [
      {
        header: t("common.table_name"),
        accessorKey: "name",
        cell: info => <div onClick={() => editTeam(info.row.original)}>{info.getValue()}</div>,
        meta: { style: { width: "50%" } },
      },
      { header: t("teams.category"), accessorKey: "category", cell: info => info.getValue() },
      { header: t("teams.age"), accessorKey: "age", cell: info => info.getValue() },
      { header: t("common.members"), accessorKey: "users_count", cell: info => info.getValue() },
      {
        header: " ",
        cell: info => (
          <div>
            <InviteMemberButton onClose={(reload) => reload} team={info.row?.original}>
              <i className="icon icon-user-follow"></i>  
            </InviteMemberButton>
            <i className="icon icon-settings" onClick={() => editTeam(info.row.original)}></i>
          </div>
        ),
        meta: { style: { width: 60 } },
      },
    ],
    []
  );

  return <Table {...{ data: teams, columns }} />;
};

const TeamsByCategory = ({ editTeam, teams, groupData, setGroupData, fetchRows }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [result, setResult] = useState({});
  const [groupTeams, setGroupTeams] = useState([]);

  useEffect(() => {
    const result = teams.reduce((acc, team) => {
      const key = team.team_group_id ?? "null";
      (acc[key] = acc[key] || []).push(team);
      return acc;
    }, {});

    for (const key in result) {
      result[key].sort((a, b) => parseFloat(a.position) - parseFloat(b.position));
    }

    setResult(result);
  }, [teams]);

  const fetchGroups = async () => {
    try {
      const { groups = [] } = await getTeamGroups();
      groups.push({ id: "null", name: t("teams.group_title"), position: "0" });
      groups.sort((a, b) => a.position - b.position);

      setGroupTeams(groups);
    } catch (error) {
      console.error("Error fetching teams:", error);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  const onDragEnd = async ({ source, destination, type }) => {
    if (!destination || (source.droppableId === destination.droppableId && source.index === destination.index)) return;

     if (type === "column") {
      const filteredGroups = groupTeams.filter(group => group.id !== "null");
      const position = calculateColumnPosition(destination.index, filteredGroups).toFixed(3);

      const [removed] = filteredGroups.splice(source.index, 1);
      filteredGroups.splice(destination.index, 0, { ...removed, position });
      filteredGroups.push(groupTeams.find(group => group.id === "null"));

      setGroupTeams(filteredGroups);
      try {
        await updateTeamGroup(removed.id, { name: removed.name, position });
      } catch (error) {
        console.error("Error updating group positions:", error);
      }
    } else {
      const startColumn = result[source.droppableId] || [];
      const endColumn = result[destination.droppableId] || [];
      const [movedItem] = startColumn.splice(source.index, 1);

      const isSameColumn = startColumn === endColumn;
      const columnToUpdate = isSameColumn ? startColumn : endColumn;
      const position = calculateRowPosition(destination.index, columnToUpdate).toFixed(3);
      columnToUpdate.splice(destination.index, 0, { ...movedItem, position });

      setResult(prevResult => ({
        ...prevResult,
        [source.droppableId]: startColumn.map((item, index) => ({ ...item, displayPosition: index + 1 })),
        [destination.droppableId]: endColumn.map((item, index) => ({ ...item, displayPosition: index + 1 })),
      }));

      const normalizedId = destination.droppableId === "null" ? null : destination.droppableId;

      try {
        await assignTeamGroup(movedItem.id, normalizedId, position);
      } catch (error) {
        console.error("Error updating team group:", error);
      }
    }
  };

  const renderHeader = useMemo(
    () => (
      <div className="group-teams-item header">
        <div className="group-icon"></div>
        <div className="group-name">{t("common.table_name")}</div>
        <div className="group-age">{t("teams.age")}</div>
        <div className="group-coach">{t("common.members")}</div>
        <div className="group-actions"></div>
      </div>
    ),
    [t]
  );

  const renderTeamRow = (team, index) => (
    <Draggable key={team.id} draggableId={'team-' + team.id.toString()} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`group-teams-item ${snapshot.isDragging ? "dragging" : ""}`}
        >
          <div className="group-icon" {...provided.dragHandleProps}>
            <i className="icon icon-options-vertical"></i>
          </div>
          <div className="group-name ellipsis pointer" onClick={() => editTeam(team)}>{team.name}</div>
          <div className="group-age">{team.age}</div>
          <div className="group-coach">{team.users_count}</div>
          <div className="group-actions">
            <InviteMemberButton onClose={(reload) => reload} team={team}>
              <i className="icon icon-user-follow"></i>  
            </InviteMemberButton>
            <i className="icon icon-settings pointer" onClick={() => editTeam(team)}></i>
          </div>
        </div>
      )}
    </Draggable>
  );

  const TeamGroup = ({ group }) => (
    <Droppable droppableId={group.id.toString()} type="task">
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.droppableProps}
          className={`group-teams ${snapshot.isDraggingOver ? "droppable-highlight" : ""}`}
        >
          {renderHeader}
          {(result[group.id] || []).map((team, index) => renderTeamRow(team, index))}
          {provided.placeholder}
        </div>
      )}
    </Droppable>
  );

  return (
    <>
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="teams-list-category">
          <div className="teams-left-column">
            <Droppable droppableId="left-column" direction="vertical" type="column">
              { provided => (
                <div ref={provided.innerRef} {...provided.droppableProps} className="teams-list-category-left">
                  {groupTeams.filter(group => group.id !== "null").map((group, index) => (
                      <Draggable key={group.id} draggableId={group.id.toString()} index={index}>
                        {provided => (
                          <div ref={provided.innerRef} {...provided.draggableProps} className="teams-list-category-item" >
                            <div {...provided.dragHandleProps} className="teams-list-category-name">
                              {group.name}
                              <i className="icon icon-pencil edit-icon pointer" onClick={() => setGroupData(group)}></i>
                            </div>
                            <TeamGroup group={group} />
                          </div>
                        )}
                      </Draggable>
                    ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </div>

          <div className="teams-right-column">
            <div className="teams-list-category-right">
              {groupTeams.filter(group => group.id === "null").map(group => (
                <div key={group.id} className="teams-list-category-item">
                  <div className="teams-list-category-name">{group.name}</div>
                    <TeamGroup group={group} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </DragDropContext>

      {groupData && 
        <GroupDialog 
          groupData={groupData} 
          close={() => setGroupData(null)} 
          fetchGroups={fetchGroups} 
          createGroup={createTeamGroup} 
          updateGroup={updateTeamGroup} 
          deleteGroup={deleteTeamGroup} 
          fetchRows={fetchRows} 
        />
      }
    </>
  );
};
